import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db, storage } from "../services/firebase";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

export default function Categories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [typesText, setTypesText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  /** 🔹 Récupération des catégories */
  async function fetchCategories() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "categoriesWine"));
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  /** 🔹 Réinitialise le formulaire */
  function resetForm() {
    setName("");
    setTypesText("");
    setEditingId(null);
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  /** 🔹 Upload d’image vers Firebase Storage */
  const uploadImage = (f) =>
    new Promise((resolve, reject) => {
      if (!f) return resolve(null);
      const name = `${Date.now()}_${f.name.replace(/\s+/g, "_")}`;
      const sref = storageRef(storage, `categories/${name}`);
      const task = uploadBytesResumable(sref, f);

      setUploading(true);
      setUploadProgress(0);

      task.on(
        "state_changed",
        (snap) =>
          setUploadProgress(
            Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          ),
        (err) => {
          setUploading(false);
          console.error(err);
          reject(err);
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            setUploading(false);
            setUploadProgress(100);
            resolve(url);
          } catch (err) {
            setUploading(false);
            reject(err);
          }
        }
      );
    });

  /** 🔹 Sauvegarde (ajout / édition) */
  async function handleSave(e) {
    e && e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      let imageUrl = null;
      if (file) imageUrl = await uploadImage(file);

      const types = typesText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      if (editingId) {
        const ref = doc(db, "categoriesWine", editingId);
        const payload = {
          name: name.trim(),
          types,
          ...(imageUrl ? { imageUrl } : {}),
        };
        await updateDoc(ref, payload);
      } else {
        await addDoc(collection(db, "categoriesWine"), {
          name: name.trim(),
          types,
          ...(imageUrl ? { imageUrl } : {}),
          createdAt: new Date(),
        });
      }

      await fetchCategories();
      resetForm();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  /** 🔹 Édition d’une catégorie */
  async function handleEdit(item) {
    setEditingId(item.id);
    setName(item.name || "");
    setTypesText((item.types || []).join(", "));
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  /** 🔹 Suppression */
  async function handleDelete(id) {
    if (!window.confirm("Supprimer cette catégorie ?")) return;
    try {
      await deleteDoc(doc(db, "categoriesWine", id));
      await fetchCategories();
    } catch (err) {
      console.error(err);
    }
  }

  /** 🔹 Rendu principal */
  return (
    <div className="container py-4">
      <h3 className="mb-3">Gestion des catégories</h3>

      <form onSubmit={handleSave} className="mb-3">
        <div className="row g-2 align-items-center">
          <div className="col-auto" style={{ minWidth: 220 }}>
            <input
              className="form-control"
              placeholder="Nom catégorie (ex: wine)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="col">
            <input
              className="form-control"
              placeholder="Types (séparés par ,) ex: red,white,rosé"
              value={typesText}
              onChange={(e) => setTypesText(e.target.value)}
            />
          </div>

          <div className="col-auto">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {uploading && (
              <div className="progress mt-1" style={{ height: 8 }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            )}
          </div>

          <div className="col-auto">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {editingId ? "Enregistrer" : "Ajouter"}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn btn-outline-secondary ms-2"
                onClick={resetForm}
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      </form>

      {loading ? (
        <div className="loading text-muted">Chargement…</div>
      ) : (
        <div className="list-group">
          {items.length === 0 && (
            <div className="list-group-item text-muted text-center">
              Aucune catégorie enregistrée.
            </div>
          )}
          {items.map((it) => (
            <div
              key={it.id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <img
                  src={it.imageUrl || "/placeholder.png"}
                  alt={it.name}
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/placeholder.png";
                  }}
                  referrerPolicy="no-referrer"
                />
                <div>
                  <strong style={{ textTransform: "capitalize" }}>
                    {it.name}
                  </strong>
                  <div className="small text-muted">
                    {(it.types || []).join(" • ")}
                  </div>
                </div>
              </div>

              <div>
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={() => handleEdit(it)}
                >
                  Éditer
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(it.id)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
